import React, { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';

import {
  RecaptchaType,
  ChallengeKind,
  ChallengeResponse,
  useRecaptcha,
  UseRecaptchaProps,
  UseRecaptchaResult,
} from './singletonHooks/useRecaptcha';

export type { ChallengeKind, ChallengeResponse, RecaptchaType };

export type RecaptchaContextProps = UseRecaptchaResult & {
  recaptchaType: RecaptchaType;
};

export const RecaptchaContext = createContext<RecaptchaContextProps | null>(null);

export const useRecaptchaContext = (): RecaptchaContextProps => {
  const context = useContext(RecaptchaContext);

  if (!context) {
    throw new Error('useRecaptchaContext must be used within a RecaptchaContextProvider');
  }

  return context;
};

type RecaptchaProviderProps = {
  children: ReactNode;
  pageId: string;
} & UseRecaptchaProps;

export const RecaptchaContextProvider = ({
  children,
  pageId,
  type,
  action,
  enabledProp,
}: RecaptchaProviderProps) => {
  const {
    isLoaded,
    enabled,
    errorMessage,
    component,
    fetchToken: baseFetchToken,
  } = useRecaptcha({
    pageId,
    type,
    action,
    enabledProp,
  });

  const fetchToken = useCallback(baseFetchToken, [baseFetchToken]);

  const state: RecaptchaContextProps = useMemo(
    () => ({
      isLoaded,
      enabled,
      errorMessage,
      component,
      fetchToken,
      recaptchaType: type,
    }),
    [isLoaded, enabled, errorMessage, component, fetchToken, type]
  );

  return <RecaptchaContext.Provider value={state}>{children}</RecaptchaContext.Provider>;
};
